'use client';

import { useState, useEffect } from 'react';
import { useTranslation } from '@/core/components/providers/TranslationProvider';
import { useSession } from 'next-auth/react';
import { ShoppingCart, Check, Info, Box } from 'lucide-react';
import { toast } from '@/core/utils/toast';
import { getPublicMediaUrl } from '@/core/utils/media';
import { trackEvent, trackAddToCartBulk, feedItemId } from '@/core/utils/analytics';
import { applyB2BTiers } from '@/core/utils/b2b-pricing';

interface QuickOrderMatrixProps {
  product: any;
}

export default function QuickOrderMatrix({ product }: QuickOrderMatrixProps) {
  const { t, currency } = useTranslation();
  const tr = t; // `t` is shadowed by tier rows below
  const { data: session } = useSession();
  const isB2B = (session?.user as any)?.type === 'b2b';
  
  const variants = product.variants || [];
  const taxRate = product.tax_rate ?? 23;

  // State to hold quantity inputs: { [variantId]: quantity }
  const [quantities, setQuantities] = useState<Record<number, number>>({});
  const [isAdding, setIsAdding] = useState(false);
  const [isAdded, setIsAdded] = useState(false);

  // If not B2B or no variants, do not render this matrix
  if (!isB2B || variants.length === 0) return null;

  // 1. Parse and extract attributes
  const attributeNames = Object.keys(variants[0]?.attributes || {});
  
  // Collect all unique values per attribute
  const attributesValuesMap: Record<string, string[]> = {};
  attributeNames.forEach((attrName) => {
    const valuesSet = new Set<string>();
    variants.forEach((v: any) => {
      if (v.attributes[attrName]) {
        valuesSet.add(v.attributes[attrName]);
      }
    });
    attributesValuesMap[attrName] = Array.from(valuesSet);
  });

  // Decide layout strategy:
  // - If exactly 2 attributes: 2D Grid
  // - Otherwise: Tabular list
  const is2DGrid = attributeNames.length === 2;

  // Prepare 2D Matrix structure if applicable
  const rowAttr = attributeNames[0];
  const colAttr = attributeNames[1];
  const rowValues = rowAttr ? attributesValuesMap[rowAttr] : [];
  const colValues = colAttr ? attributesValuesMap[colAttr] : [];

  // Helper to find a variant by attributes
  const findVariantByAttributes = (attrs: Record<string, string>) => {
    return variants.find((v: any) => {
      return Object.entries(attrs).every(([key, val]) => v.attributes[key] === val) &&
             Object.keys(v.attributes).length === Object.keys(attrs).length;
    });
  };

  // Helper to calculate dynamic B2B unit price for a given variant and quantity (net/gross)
  const getDynamicVariantPrice = (variant: any, qty: number) => {
    let price = variant.price || 0.0;
    if (variant.sales_price !== null && variant.sales_price !== undefined) {
      price = variant.sales_price;
    }

    // Apply tier pricing overlays if available
    // Progi tego klienta z API (cennik grupy + progi z karty produktu), już w aktywnej walucie.
    const tierPrices = variant.b2b_tiers || [];
    let isTierActive = false;
    let originalB2BPrice = price;

    if (tierPrices.length > 0 && qty > 0) {
      const qualifyingTiers = tierPrices
        .map((t: any) => ({ min_qty: parseInt(t.min_qty), price: parseFloat(t.price) }))
        .filter((t: any) => t.min_qty > 0 && qty >= t.min_qty && !isNaN(t.price));

      if (qualifyingTiers.length > 0) {
        qualifyingTiers.sort((a: any, b: any) => b.min_qty - a.min_qty);
        price = qualifyingTiers[0].price;
        isTierActive = true;
      }
    }

    // Polish B2B displays prices in Net by default
    const divisor = 1 + taxRate / 100;
    const netPrice = price / divisor;
    const originalNetPrice = originalB2BPrice / divisor;

    return {
      unitNet: netPrice,
      unitGross: price,
      isTierActive,
      originalNet: originalNetPrice
    };
  };

  const handleQtyChange = (variantId: number, val: string, stock: number) => {
    const qty = val === '' ? 0 : parseInt(val);
    if (isNaN(qty) || qty < 0) return;
    
    // Cap quantity by available stock
    const finalQty = Math.min(qty, stock);
    
    setQuantities(prev => ({
      ...prev,
      [variantId]: finalQty
    }));
  };

  // Calculations for total summary
  let totalItemsCount = 0;
  let totalNetPrice = 0;
  let totalGrossPrice = 0;

  Object.entries(quantities).forEach(([vIdStr, qty]) => {
    if (qty <= 0) return;
    const vId = parseInt(vIdStr);
    const variant = variants.find((v: any) => v.id === vId);
    if (!variant) return;

    const prices = getDynamicVariantPrice(variant, qty);
    totalItemsCount += qty;
    totalNetPrice += prices.unitNet * qty;
    totalGrossPrice += prices.unitGross * qty;
  });

  const totalVat = totalGrossPrice - totalNetPrice;

  // Add all selected products to the cart in bulk
  const handleBulkAddToCart = () => {
    const itemsToAdd = Object.entries(quantities).filter(([_, qty]) => qty > 0);
    if (itemsToAdd.length === 0) {
      toast.error('Wprowadź ilość dla co najmniej jednego wariantu.');
      return;
    }

    setIsAdding(true);
    try {
      const cart = JSON.parse(localStorage.getItem('stag-cart') || '[]');
      const mainImage = product.images?.find((img: any) => img.is_main) || product.images?.[0];
      const imageUrl = mainImage ? getPublicMediaUrl(mainImage.path) : null;

      const bulkItemsToTrack: any[] = [];
      const categoryName = product.category_path && product.category_path.length > 0 
        ? product.category_path[product.category_path.length - 1]?.name 
        : (product.categories && product.categories.length > 0 ? product.categories[product.categories.length - 1]?.name : '');

      itemsToAdd.forEach(([vIdStr, qty]) => {
        const vId = parseInt(vIdStr);
        const variant = variants.find((v: any) => v.id === vId);
        if (!variant) return;

        const prices = getDynamicVariantPrice(variant, qty);
        const cartItemId = `${product.id}-${variant.id}`;
        
        // Build clear name showing attributes
        const variantLabel = Object.values(variant.attributes).join(', ');
        const name = `${product.name} (${variantLabel})`;

        const existingItem = cart.find((item: any) => item.cartItemId === cartItemId);
        if (existingItem) {
          existingItem.quantity = Math.min(existingItem.quantity + qty, variant.stock);
          // Update to dynamically resolved price (e.g. tier price if it changed)
          existingItem.price = prices.unitGross;
          if (variant.b2b_tiers?.length) {
            Object.assign(existingItem, applyB2BTiers({ ...existingItem, b2b_base_price: variant.price, b2b_tiers: variant.b2b_tiers }));
          }
        } else {
          cart.push({
            cartItemId,
            id: product.id,
            variant_id: variant.id,
            name,
            price: prices.unitGross, // standard cart works with gross prices
            image: imageUrl,
            slug: product.slug,
            quantity: qty,
            attributes: variant.attributes,
            tax_rate: taxRate,
            ...(variant.b2b_tiers?.length ? { b2b_base_price: variant.price, b2b_tiers: variant.b2b_tiers } : {}),
          });
        }

        bulkItemsToTrack.push({
          item_id: feedItemId(product, variant),
          item_name: name,
          price: prices.unitGross,
          quantity: qty,
          item_category: categoryName || ''
        });
      });

      localStorage.setItem('stag-cart', JSON.stringify(cart));

      // Track bulk items (both Meta Pixel and Google dataLayer)
      if (bulkItemsToTrack.length > 0) {
        trackAddToCartBulk(bulkItemsToTrack, currency || 'PLN');
      }

      setIsAdded(true);
      window.dispatchEvent(new Event('cart-updated'));
      toast.success(`Pomyślnie dodano ${totalItemsCount} szt. do koszyka!`);
      
      // Reset inputs
      setQuantities({});
      
      setTimeout(() => setIsAdded(false), 2500);
    } catch (err) {
      console.error(err);
      toast.error('Błąd podczas dodawania do koszyka.');
    } finally {
      setIsAdding(false);
    }
  };

  return (
    <div className="w-full bg-slate-900/5 dark:bg-white/5 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 shadow-sm backdrop-blur-sm space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <h2 className="text-xl font-bold text-slate-800 dark:text-slate-100">
              Siatka Zamówień Hurtowych B2B
            </h2>
            <span className="text-[10px] uppercase font-bold tracking-widest px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20">
              Hurt (Netto)
            </span>
          </div>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            Wpisz ilości dla poszczególnych wariantów i dodaj je zbiorczo jednym kliknięciem. Ceny uwzględniają rabaty i progi ilościowe.
          </p>
        </div>
      </div>

      {is2DGrid ? (
        /* 2D Matrix Layout (e.g. Size vs Color) */
        <div className="overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950">
          <table className="w-full border-collapse text-left text-sm">
            <thead>
              <tr className="bg-slate-50 dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 text-slate-600 dark:text-slate-300 font-semibold">
                <th className="p-4 border-r border-slate-200 dark:border-slate-800 font-extrabold text-xs uppercase tracking-wider text-slate-400">
                  {rowAttr} \ {colAttr}
                </th>
                {colValues.map((colVal) => (
                  <th key={colVal} className="p-4 text-center font-bold">
                    <span className="inline-block px-2.5 py-1 rounded-md bg-slate-100 dark:bg-slate-800 text-xs text-slate-700 dark:text-slate-200 font-medium shadow-sm">
                      {colVal}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rowValues.map((rowVal) => (
                <tr
                  key={rowVal}
                  className="border-b border-slate-200 dark:border-slate-800 hover:bg-slate-50/50 dark:hover:bg-slate-900/20 transition-colors"
                >
                  <td className="p-4 font-bold text-slate-800 dark:text-slate-200 border-r border-slate-200 dark:border-slate-800 bg-slate-50/30 dark:bg-slate-900/10">
                    {rowVal}
                  </td>
                  {colValues.map((colVal) => {
                    const variant = findVariantByAttributes({
                      [rowAttr]: rowVal,
                      [colAttr]: colVal,
                    });

                    if (!variant) {
                      return (
                        <td key={colVal} className="p-4 text-center text-slate-300 dark:text-slate-700 italic">
                          —
                        </td>
                      );
                    }

                    const qty = quantities[variant.id] || 0;
                    const prices = getDynamicVariantPrice(variant, qty);
                    const isOutOfStock = variant.stock <= 0;

                    return (
                      <td key={colVal} className="p-4 text-center">
                        <div className="flex flex-col items-center gap-2">
                          {/* Price representation */}
                          <div className="h-9 flex flex-col justify-center items-center">
                            {prices.isTierActive ? (
                              <>
                                <span className="text-[10px] text-muted-foreground line-through">
                                  {prices.originalNet.toFixed(2)} {currency}
                                </span>
                                <span className="text-xs font-bold text-emerald-600 dark:text-emerald-400 animate-pulse">
                                  {prices.unitNet.toFixed(2)} {currency}
                                </span>
                              </>
                            ) : (
                              <span className="text-xs font-semibold text-slate-700 dark:text-slate-300">
                                {prices.unitNet.toFixed(2)} {currency}
                              </span>
                            )}
                          </div>

                          {/* Qty Input */}
                          <div className="relative w-20">
                            <input
                              type="number"
                              min="0"
                              max={variant.stock}
                              disabled={isOutOfStock}
                              value={qty || ''}
                              placeholder="0"
                              onChange={(e) => handleQtyChange(variant.id, e.target.value, variant.stock)}
                              className={`w-full text-center px-2 py-1.5 rounded-lg border text-sm font-semibold transition-all ${
                                qty > 0
                                  ? 'border-primary ring-2 ring-primary/20 bg-primary/5 text-slate-800 dark:text-slate-100'
                                  : isOutOfStock
                                  ? 'border-slate-200 bg-slate-50 text-slate-400 cursor-not-allowed dark:border-slate-800 dark:bg-slate-900/50'
                                  : 'border-slate-300 bg-white text-slate-700 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-300 focus:border-slate-900 focus:ring-1 focus:ring-slate-900'
                              }`}
                            />
                          </div>

                          {/* Stock badge */}
                          <div className="flex items-center gap-1">
                            <div
                              className={`w-1.5 h-1.5 rounded-full ${
                                isOutOfStock
                                  ? 'bg-red-500'
                                  : variant.stock > 10
                                  ? 'bg-green-500'
                                  : 'bg-amber-500'
                              }`}
                            />
                            <span className="text-[10px] text-slate-400 dark:text-slate-500 font-medium">
                              {isOutOfStock ? 'Brak' : `${variant.stock} szt.`}
                            </span>
                          </div>

                          {/* Tier tooltip helper info if available */}
                          {variant.b2b_tiers?.length > 0 && !prices.isTierActive && (
                            <div className="flex items-center gap-0.5 text-[9px] text-primary/80">
                              <Info className="w-2.5 h-2.5 shrink-0" />
                              <span>Progi</span>
                            </div>
                          )}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        /* Tabular List Layout (for 1 attribute or 3+ attributes) */
        <div className="overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950">
          <table className="w-full border-collapse text-left text-sm">
            <thead>
              <tr className="bg-slate-50 dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 text-slate-600 dark:text-slate-300 font-semibold">
                <th className="p-4">Kod wariantu (SKU)</th>
                <th className="p-4">Atrybuty</th>
                <th className="p-4 text-center">Magazyn</th>
                <th className="p-4 text-right">Cena jedn. (Netto)</th>
                <th className="p-4 text-center w-28">Ilość</th>
                <th className="p-4 text-right w-36">Wartość</th>
              </tr>
            </thead>
            <tbody>
              {variants.map((variant: any) => {
                const qty = quantities[variant.id] || 0;
                const prices = getDynamicVariantPrice(variant, qty);
                const isOutOfStock = variant.stock <= 0;
                const rowTotal = prices.unitNet * qty;

                // Create clean text representation of attributes
                const attributesLabel = Object.entries(variant.attributes)
                  .map(([k, v]) => `${k}: ${v}`)
                  .join(' | ');

                return (
                  <tr
                    key={variant.id}
                    className="border-b border-slate-200 dark:border-slate-800 hover:bg-slate-50/50 dark:hover:bg-slate-900/20 transition-colors"
                  >
                    <td className="p-4 font-mono font-bold text-xs text-slate-400">
                      {variant.sku || `VAR-${variant.id}`}
                    </td>
                    <td className="p-4 font-semibold text-slate-700 dark:text-slate-200">
                      {attributesLabel}
                    </td>
                    <td className="p-4 text-center">
                      <div className="inline-flex items-center gap-1.5">
                        <div
                          className={`w-1.5 h-1.5 rounded-full ${
                            isOutOfStock
                              ? 'bg-red-500'
                              : variant.stock > 10
                              ? 'bg-green-500'
                              : 'bg-amber-500'
                          }`}
                        />
                        <span className="text-xs text-slate-600 dark:text-slate-400 font-medium">
                          {isOutOfStock ? 'Brak na stanie' : `${variant.stock} szt.`}
                        </span>
                      </div>
                    </td>
                    <td className="p-4 text-right">
                      <div className="flex flex-col items-end">
                        {prices.isTierActive ? (
                          <>
                            <span className="text-[10px] text-muted-foreground line-through">
                              {prices.originalNet.toFixed(2)} {currency}
                            </span>
                            <span className="text-xs font-bold text-emerald-600 dark:text-emerald-400">
                              {prices.unitNet.toFixed(2)} {currency}
                            </span>
                          </>
                        ) : (
                          <span className="text-sm font-semibold text-slate-800 dark:text-slate-200">
                            {prices.unitNet.toFixed(2)} {currency}
                          </span>
                        )}
                        {variant.b2b_tiers?.length > 0 && !prices.isTierActive && (
                          <span className="text-[10px] text-primary/80 font-medium flex items-center gap-0.5 mt-0.5">
                            <Info className="w-2.5 h-2.5 shrink-0" /> Dostępne progi hurtowe
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="p-4 text-center">
                      <input
                        type="number"
                        min="0"
                        max={variant.stock}
                        disabled={isOutOfStock}
                        value={qty || ''}
                        placeholder="0"
                        onChange={(e) => handleQtyChange(variant.id, e.target.value, variant.stock)}
                        className={`w-full text-center px-2 py-1.5 rounded-lg border text-sm font-bold transition-all ${
                          qty > 0
                            ? 'border-primary ring-2 ring-primary/20 bg-primary/5 text-slate-800 dark:text-slate-100'
                            : isOutOfStock
                            ? 'border-slate-200 bg-slate-50 text-slate-400 cursor-not-allowed dark:border-slate-800 dark:bg-slate-900/50'
                            : 'border-slate-300 bg-white text-slate-700 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-300 focus:border-slate-900 focus:ring-1 focus:ring-slate-900'
                        }`}
                      />
                    </td>
                    <td className="p-4 text-right font-bold text-slate-900 dark:text-slate-100">
                      {qty > 0 ? `${rowTotal.toFixed(2)} ${currency}` : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Tier Price List Info (Tooltip / Helper Box) */}
      <div className="bg-slate-50 dark:bg-slate-900/50 p-4 rounded-xl border border-slate-100 dark:border-slate-800/80">
        <h4 className="text-xs font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wider flex items-center gap-1.5 mb-2">
          <Box className="w-3.5 h-3.5 text-primary" /> Polityka Progów Hurtowych
        </h4>
        <div className="text-xs text-slate-500 dark:text-slate-400 space-y-1.5">
          <p>
            System automatycznie aktywuje niższe ceny jednostkowe po osiągnięciu odpowiednich progów ilościowych dla danej pozycji:
          </p>
          <div className="flex flex-wrap gap-2 pt-1">
            {variants.some((v: any) => v.b2b_tiers?.length > 0) ? (
              variants.reduce((acc: any[], v: any) => {
                (v.b2b_tiers || []).forEach((t: any) => {
                  const label = `${tr('product.b2b_tier_from', 'od {n} szt.').replace('{n}', String(t.min_qty))} → ${(parseFloat(t.price) / (1 + taxRate/100)).toFixed(2)} ${currency} ${tr('product.b2b_net', 'netto')}`;
                  if (!acc.includes(label)) acc.push(label);
                });
                return acc;
              }, []).map((label: string) => (
                <span key={label} className="inline-flex items-center gap-1 px-2.5 py-1 rounded bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 font-medium text-[10px] text-slate-600 dark:text-slate-300 shadow-sm">
                  {label}
                </span>
              ))
            ) : (
              <span className="italic text-slate-400">Ten produkt nie ma zdefiniowanych indywidualnych progów ilościowych. Obowiązuje stały rabat B2B grupy.</span>
            )}
          </div>
        </div>
      </div>

      {/* Dynamic Summary Bar */}
      <div className="bg-slate-900 text-white rounded-xl p-5 shadow-lg flex flex-col md:flex-row items-center justify-between gap-6 border border-slate-800">
        <div className="grid grid-cols-2 md:flex md:items-center gap-x-6 gap-y-2 w-full md:w-auto text-left">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Wybrane warianty</p>
            <p className="text-xl font-extrabold text-slate-100">{totalItemsCount} szt.</p>
          </div>
          <div className="md:border-l md:border-slate-800 md:pl-6">
            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Wartość Netto</p>
            <p className="text-xl font-extrabold text-slate-100">{totalNetPrice.toFixed(2)} {currency}</p>
          </div>
          <div className="md:border-l md:border-slate-800 md:pl-6">
            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Szacowany VAT (23%)</p>
            <p className="text-xl font-bold text-slate-300">{totalVat.toFixed(2)} {currency}</p>
          </div>
          <div className="md:border-l md:border-slate-800 md:pl-6">
            <p className="text-[10px] font-bold uppercase tracking-wider text-emerald-400">Suma Brutto</p>
            <p className="text-2xl font-black text-emerald-400">{totalGrossPrice.toFixed(2)} {currency}</p>
          </div>
        </div>

        <button
          type="button"
          onClick={handleBulkAddToCart}
          disabled={totalItemsCount === 0 || isAdding}
          className={`flex items-center justify-center gap-2 px-8 py-3.5 rounded-xl font-extrabold text-sm uppercase tracking-wider transition-all duration-300 shadow-md w-full md:w-auto ${
            isAdded
              ? 'bg-emerald-500 text-white hover:bg-emerald-600 scale-105'
              : totalItemsCount === 0
              ? 'bg-slate-800 text-slate-500 cursor-not-allowed'
              : 'bg-primary hover:bg-primary/90 text-white hover:scale-105 active:scale-95'
          }`}
        >
          {isAdded ? (
            <>
              <Check className="h-5 w-5 shrink-0 animate-bounce" />
              <span>Dodano do koszyka!</span>
            </>
          ) : (
            <>
              <ShoppingCart className="h-5 w-5 shrink-0" />
              <span>Dodaj wybrane ({totalItemsCount}) do koszyka</span>
            </>
          )}
        </button>
      </div>
    </div>
  );
}
